import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  PlayRoomGameResponse,
  RoomGamePrizeSummary,
  RoomGamesConfigResponse,
  RoomGameType,
  SpendWalletBalance,
} from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth, useAuthModal } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { useCoinShop } from '../wallet/index.js';

import {
  PlayerMenuDock,
  type PlayerMenuDockItem,
  type PlayerMenuDockRank,
} from './PlayerMenuDock.js';
import { SlotMachineOverlay, SpinWheelOverlay } from './RoomGameOverlays.js';

/** Competitive stubs until dedicated engines ship. */
const COMPETITIVE_GAMES: ReadonlyArray<Omit<PlayerMenuDockItem, 'disabled'>> = [
  { id: 'raffle', label: 'Raffle entry', amountCoins: 20 },
  { id: 'crown-challenge', label: 'Crown challenge', amountCoins: 100 },
  { id: 'high-roller', label: 'High roller duel', amountCoins: 50 },
] as const;

const SAMPLE_ROOM_RANKS: ReadonlyArray<PlayerMenuDockRank> = [
  { rank: 1, label: 'vipnova', detail: '1,840 pts' },
  { rank: 2, label: 'coilking', detail: '1,120 pts' },
  { rank: 3, label: 'nightshade', detail: '760 pts' },
  { rank: 4, label: 'pulsefan', detail: '410 pts' },
  { rank: 5, label: 'greyuser', detail: '180 pts' },
] as const;

function withAffordability(
  games: ReadonlyArray<Omit<PlayerMenuDockItem, 'disabled'>>,
  canTip: boolean,
  isBusy: boolean,
): PlayerMenuDockItem[] {
  return games.map((game) => ({
    ...game,
    disabled: isBusy || !canTip,
  }));
}

export interface GamesMenuBarProps {
  showId: string | null;
  modelUsername: string;
  canTip: boolean;
  coinBalance: number;
  isTipping: boolean;
  onSendTip: (amountCoins: number, message?: string) => Promise<boolean>;
  onGamePlayed: (wallet: SpendWalletBalance) => void;
  onRefreshChat: () => void;
  playerEl: HTMLElement | null;
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
}

export function GamesMenuBar({
  showId,
  modelUsername,
  canTip,
  coinBalance,
  isTipping,
  onSendTip,
  onGamePlayed,
  onRefreshChat,
  playerEl,
  menuOpen,
  onMenuOpenChange,
}: GamesMenuBarProps): ReactElement {
  const { api } = useAuth();
  const { requireAuth } = useAuthModal();
  const { requireCoins } = useCoinShop();
  const [config, setConfig] = useState<RoomGamesConfigResponse | null>(null);
  const [activeGame, setActiveGame] = useState<RoomGameType | null>(null);
  const [playResult, setPlayResult] = useState<PlayRoomGameResponse | null>(null);
  const [playError, setPlayError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const loadConfig = useCallback(() => {
    void api
      .getModelRoomGames(modelUsername)
      .then((result) => {
        setConfig(result);
      })
      .catch(() => {
        setConfig(null);
      });
  }, [api, modelUsername]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const casualGames = useMemo((): Omit<PlayerMenuDockItem, 'disabled'>[] => {
    if (!config) {
      return [
        { id: 'spin-wheel', label: t('common.show.spinWheelTitle'), amountCoins: 8 },
        { id: 'slot-machine', label: t('common.show.slotTitle'), amountCoins: 12 },
      ];
    }
    const games: Omit<PlayerMenuDockItem, 'disabled'>[] = [];
    if (config.settings.wheelEnabled) {
      games.push({
        id: 'spin-wheel',
        label: t('common.show.spinWheelTitle'),
        amountCoins: config.settings.wheelCostCoins,
      });
    }
    if (config.settings.slotEnabled) {
      games.push({
        id: 'slot-machine',
        label: t('common.show.slotTitle'),
        amountCoins: config.settings.slotCostCoins,
      });
    }
    return games;
  }, [config]);

  const prizesFor = (gameType: RoomGameType): RoomGamePrizeSummary[] =>
    (config?.prizes ?? []).filter((prize) => prize.gameType === gameType && prize.enabled);

  const openGame = (gameType: RoomGameType): void => {
    setPlayResult(null);
    setPlayError(null);
    setActiveGame(gameType);
    onMenuOpenChange(false);
  };

  const playGame = (item: PlayerMenuDockItem): void => {
    requireAuth(() => {
      if (item.id === 'spin-wheel') {
        openGame('WHEEL');
        return;
      }
      if (item.id === 'slot-machine') {
        openGame('SLOT');
        return;
      }
      if (!requireCoins(coinBalance, item.amountCoins, () => {
        playGame(item);
      })) {
        return;
      }
      void onSendTip(item.amountCoins, item.label).then((sent) => {
        if (sent) {
          onMenuOpenChange(false);
        }
      });
    }, 'game');
  };

  const handlePlay = async (): Promise<void> => {
    if (!showId || !activeGame) {
      setPlayError(t('common.show.gameNeedLive'));
      return;
    }

    const cost =
      activeGame === 'WHEEL'
        ? (config?.settings.wheelCostCoins ?? 8)
        : (config?.settings.slotCostCoins ?? 12);
    if (!requireCoins(coinBalance, cost, () => {
      void handlePlay();
    })) {
      return;
    }

    setIsPlaying(true);
    setPlayError(null);
    try {
      const result = await api.playRoomGame(showId, { gameType: activeGame });
      setPlayResult(result);
      onGamePlayed(result.wallet);
      onRefreshChat();
    } catch (err) {
      setPlayError(err instanceof ApiError ? err.message : t('common.show.gamePlayError'));
    } finally {
      setIsPlaying(false);
    }
  };

  const busy = isTipping || isPlaying;

  return (
    <>
      <Button
        label={t('common.show.gamesMenuButton')}
        variant="games"
        onClick={() => {
          requireAuth(() => {
            loadConfig();
            onMenuOpenChange(true);
          }, 'game');
        }}
      />

      {menuOpen && playerEl
        ? createPortal(
            <PlayerMenuDock
              titleId="games-menu-dock-title"
              title={t('common.show.gamesMenuTitle')}
              hint={t('common.show.gamesMenuHint')}
              emptyLabel={t('common.show.gamesMenuEmpty')}
              closeLabel={t('common.show.gamesMenuClose')}
              sections={[
                {
                  heading: t('common.show.gamesMenuCasual'),
                  items: withAffordability(casualGames, canTip, busy),
                },
                {
                  heading: t('common.show.gamesMenuCompetitive'),
                  items: withAffordability(
                    import.meta.env.DEV ? COMPETITIVE_GAMES : [],
                    canTip,
                    busy,
                  ),
                },
              ]}
              ranksHeading={t('common.show.gamesMenuLeaderboard')}
              ranksEmptyLabel={t('common.show.gamesMenuLeaderboardEmpty')}
              ranks={[...SAMPLE_ROOM_RANKS]}
              onSelect={playGame}
              onClose={() => {
                onMenuOpenChange(false);
              }}
            />,
            playerEl,
          )
        : null}

      {activeGame === 'WHEEL' && playerEl
        ? createPortal(
            <SpinWheelOverlay
              costCoins={config?.settings.wheelCostCoins ?? 8}
              prizes={prizesFor('WHEEL')}
              busy={busy}
              result={playResult}
              error={playError}
              onPlay={() => {
                void handlePlay();
              }}
              onClose={() => {
                setActiveGame(null);
                setPlayResult(null);
                setPlayError(null);
              }}
            />,
            playerEl,
          )
        : null}

      {activeGame === 'SLOT' && playerEl
        ? createPortal(
            <SlotMachineOverlay
              costCoins={config?.settings.slotCostCoins ?? 12}
              prizes={prizesFor('SLOT')}
              busy={busy}
              result={playResult}
              error={playError}
              onPlay={() => {
                void handlePlay();
              }}
              onClose={() => {
                setActiveGame(null);
                setPlayResult(null);
                setPlayError(null);
              }}
            />,
            playerEl,
          )
        : null}
    </>
  );
}
